import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ReportCard, ReportCardSection } from "../../types";
import { getReportCard } from "../../services/evaluatorService";
import ReportCardHeader from "./ReportCardHeader";
import CriterionRow from "./CriterionRow";
import BandAxis from "./BandAxis";
import BandRules from "./BandRules";
import {
  bandBoundaries,
  bandFor,
  colorForIndex,
  groupByKind,
  normalisedWeights,
  sharedBands,
  trackGradient,
  weakestCriterionId,
  weightedGrade,
} from "./reportCardModel";
import "./report-card.css";

interface Arrival {
  criterionId: string;
  nonce: number;
}

interface KindBlockProps {
  title: string;
  scope: string;
  scaleNote: string;
  sections: ReportCardSection[];
  /** Where this kind's colours pick up, so no hue repeats across the two kinds. */
  colorOffset: number;
  flaggedCriterionId: string | null;
  arrival: Arrival | null;
}

const KindBlock: React.FC<KindBlockProps> = ({
  title,
  scope,
  scaleNote,
  sections,
  colorOffset,
  flaggedCriterionId,
  arrival,
}) => {
  const weights = normalisedWeights(sections);
  // Shared bands govern only what one strip can say for every row: the band
  // names on the axis, the continuous rules behind the gauges, and the band the
  // weighted rollup lands in. Each row's own tinting comes from its own levels.
  const bands = sharedBands(sections);
  const grade = weightedGrade(sections);
  const band = bands && grade !== null ? bandFor(bands, grade) : null;

  return (
    <section className="block">
      <div className="block-head">
        <h2>{title}</h2>
        <p className="scope">{scope}</p>
      </div>

      <BandAxis bands={bands} note={scaleNote} />

      <div className="rows">
        <BandRules bands={bands} />
        {sections.map((section, i) => (
          <CriterionRow
            key={section.criterionId}
            section={section}
            color={colorForIndex(colorOffset + i)}
            weightShare={weights[i]}
            index={colorOffset + i}
            trackBackground={trackGradient(
              bandBoundaries(section.performanceLevels),
            )}
            flagged={section.criterionId === flaggedCriterionId}
            arrivalNonce={
              arrival?.criterionId === section.criterionId ? arrival.nonce : 0
            }
          />
        ))}
      </div>

      <div className="lane rollup">
        <p className="rollup-label">
          Weighted {band && <b>{band.name}</b>}
        </p>
        <div
          className="rollup-mark"
          style={{ "--g": grade ?? 0 } as React.CSSProperties}
        >
          <i />
          <span>{grade === null ? "—" : grade.toFixed(1)}</span>
        </div>
      </div>
    </section>
  );
};

interface Props {
  getToken: () => string | undefined;
  /** Injected in place of a fetch when previewing the layout. */
  card?: ReportCard;
  /**
   * Hide "Back to session". That button goes to the learner's chat route,
   * which is wrong when the card is embedded in the admin session viewer.
   */
  hideBackToChat?: boolean;
}

const ReportCardPage: React.FC<Props> = ({ getToken, card: injected, hideBackToChat = false }) => {
  const navigate = useNavigate();
  const { orgSlug, sessionId } = useParams<{ orgSlug: string; sessionId: string }>();

  const [card, setCard] = useState<ReportCard | null>(injected ?? null);
  const [status, setStatus] = useState<"loading" | "ready" | "missing" | "error">(
    injected ? "ready" : "loading",
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (injected || !sessionId) return;
    let cancelled = false;
    setStatus("loading");
    getReportCard(sessionId, getToken())
      .then((result) => {
        if (cancelled) return;
        setCard(result);
        setStatus(result ? "ready" : "missing");
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Could not load this debrief.");
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [injected, sessionId, getToken]);

  const [arrival, setArrival] = useState<Arrival | null>(null);

  const flagged = useMemo(
    () => (card ? weakestCriterionId(card.sections) : null),
    [card],
  );

  const jumpToCriterion = useCallback((criterionId: string) => {
    const target = document.getElementById(`criterion-${criterionId}`);
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    target?.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "center" });
    setArrival((previous) => ({
      criterionId,
      nonce: (previous?.criterionId === criterionId ? previous.nonce : 0) + 1,
    }));
  }, []);

  const back = hideBackToChat ? null : (
    <button
      type="button"
      onClick={() => navigate(`/${orgSlug}/chat/${sessionId}`)}
      className="mb-8 flex items-center gap-1 text-xs font-label uppercase tracking-wider text-slate-400 transition-colors hover:text-on-surface"
    >
      <span className="material-symbols-outlined text-base">arrow_back</span>
      Back to session
    </button>
  );

  if (status !== "ready" || !card) {
    return (
      <div className="report-card flex min-w-0 flex-1 flex-col overflow-y-auto">
        <div className="shell">
          {back}
          <p className="font-label text-sm uppercase tracking-widest text-slate-400">
            {status === "loading" && "Loading debrief…"}
            {status === "missing" &&
              "This session has no debrief yet. Submit an answer to generate one."}
            {status === "error" && (error ?? "Could not load this debrief.")}
          </p>
        </div>
      </div>
    );
  }

  const { conversation, solution } = groupByKind(card.sections);

  return (
    // The app shell is h-screen/overflow-hidden and body sets overflow:hidden,
    // so this page has to own its scroll container rather than expecting the
    // document to scroll.
    <div className="report-card flex min-w-0 flex-1 flex-col overflow-y-auto">
      <div className="shell">
        {back}

        <ReportCardHeader card={card} onSelectCriterion={jumpToCriterion} />

        <main>
          {conversation.length > 0 && (
            <KindBlock
              title="Conversation"
              scope="Graded on the discovery dialogue only"
              scaleNote="Every criterion is plotted on one 0–100 scale. Bar thickness shows how much the criterion counts toward the weighted grade."
              sections={conversation}
              colorOffset={0}
              flaggedCriterionId={flagged}
              arrival={arrival}
            />
          )}
          {solution.length > 0 && (
            <KindBlock
              title="Solution"
              scope="Graded on the submitted answer and its artifacts"
              scaleNote="Solution criteria carry their own weights, normalised separately from the conversation."
              sections={solution}
              colorOffset={conversation.length}
              flaggedCriterionId={flagged}
              arrival={arrival}
            />
          )}
        </main>

        <footer className="colophon">
          <p>
            This debrief reports two weighted grades and no combined score. Conversation and
            solution are graded against separate criteria, weighted separately, and averaging
            them would hide the thing the two lowest scores have in common.
          </p>
          <p className="sig">
            Graded by <b>Pinstripe Evaluator</b>
            <br />
            Rubric <b>{card.rubricName}</b>
            <br />
            Bands set by the rubric author
          </p>
        </footer>
      </div>
    </div>
  );
};

export default ReportCardPage;
